import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useVoiceChat } from './VoiceChatButton';
import { corporateTheme, corporateLabels } from '../themes/corporate';

const HeroContainer = styled.div`
  width: 100%;
  min-height: 100vh;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  padding-top: 80px;
  background: ${corporateTheme.backgroundGradient};
  font-family: ${corporateTheme.fontFamily};
`;

const BackgroundImage = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, rgba(248, 250, 252, 0.95) 0%, rgba(241, 245, 249, 0.9) 50%, rgba(226, 232, 240, 0.95) 100%),
              url('https://images.unsplash.com/photo-1579621970563-ebec7560ff3e?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80');
  background-size: cover;
  background-position: center;
  background-attachment: fixed;
  filter: brightness(1.1) contrast(0.8);
`;

const HeroContent = styled.div`
  text-align: center;
  z-index: 10;
  max-width: 1200px;
  padding: 0 2rem;
  position: relative;
`;

const Badge = styled(motion.div)`
  display: inline-block;
  background: rgba(30, 64, 175, 0.1);
  border: 1px solid rgba(30, 64, 175, 0.3);
  padding: 0.5rem 1.5rem;
  border-radius: 25px;
  font-size: 0.9rem;
  color: ${corporateTheme.primary};
  font-weight: 600;
  margin-bottom: 2rem;
  text-transform: uppercase;
  letter-spacing: 1px;
`;

const MainHeadline = styled(motion.h1)`
  font-size: clamp(2rem, 6vw, 4rem);
  font-weight: 900;
  color: ${corporateTheme.textPrimary};
  margin-bottom: 1rem;
  line-height: 1.1;
`;

const EarningHighlight = styled(motion.div)`
  display: flex;
  justify-content: center;
  gap: 3rem;
  margin: 2rem 0;
  flex-wrap: wrap;
`;

const EarningItem = styled(motion.div)`
  text-align: center;
`;

const EarningLabel = styled.div`
  font-size: 1rem;
  color: ${corporateTheme.textTertiary};
  margin-bottom: 0.5rem;
  text-transform: uppercase;
  letter-spacing: 1px;
`;

const EarningAmount = styled.div`
  font-size: clamp(3rem, 8vw, 6rem);
  font-weight: 900;
  background: ${corporateTheme.primaryGradient};
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  line-height: 1;
`;

const EarningDescription = styled.div`
  font-size: 1rem;
  color: ${corporateTheme.textSecondary};
  margin-top: 0.5rem;
`;

const Description = styled(motion.p)`
  font-size: clamp(1.1rem, 2vw, 1.3rem);
  color: ${corporateTheme.textSecondary};
  max-width: 700px;
  margin: 2rem auto;
  line-height: 1.7;
`;

const ButtonGroup = styled(motion.div)`
  display: flex;
  gap: 1.5rem;
  justify-content: center;
  flex-wrap: wrap;
  margin: 3rem 0;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: center;
    gap: 1rem;
    margin: 2rem 0;
  }
`;

const PrimaryButton = styled(motion.button)`
  background: ${corporateTheme.primaryGradient};
  color: ${corporateTheme.background};
  border: none;
  padding: 1.2rem 3rem;
  font-size: 1.2rem;
  font-weight: 700;
  border-radius: 50px;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: ${corporateTheme.cardShadow};
  text-transform: uppercase;
  letter-spacing: 1px;
  white-space: nowrap;
  
  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 15px 40px rgba(30, 64, 175, 0.3);
    background: linear-gradient(135deg, ${corporateTheme.primaryDark} 0%, ${corporateTheme.primary} 100%);
  }
  
  @media (max-width: 768px) {
    padding: 1rem 2rem;
    font-size: 1rem;
    white-space: normal;
    text-align: center;
    min-width: 280px;
    max-width: 90vw;
  }
  
  @media (max-width: 480px) {
    padding: 0.9rem 1.5rem;
    font-size: 0.9rem;
    min-width: 250px;
    letter-spacing: 0.5px;
  }
`;

const SecondaryButton = styled(motion.button)`
  background: transparent;
  color: ${corporateTheme.textPrimary};
  border: 2px solid ${corporateTheme.cardBorder};
  padding: 1.2rem 3rem;
  font-size: 1.2rem;
  font-weight: 600;
  border-radius: 50px;
  cursor: pointer;
  transition: all 0.3s ease;
  backdrop-filter: blur(10px);
  white-space: nowrap;
  
  &:hover {
    border-color: ${corporateTheme.primary};
    color: ${corporateTheme.primary};
    transform: translateY(-3px);
    box-shadow: 0 10px 30px rgba(30, 64, 175, 0.2);
  }
  
  @media (max-width: 768px) {
    padding: 1rem 2rem;
    font-size: 1rem;
    white-space: normal;
    text-align: center;
    min-width: 280px;
    max-width: 90vw;
  }
  
  @media (max-width: 480px) {
    padding: 0.9rem 1.5rem;
    font-size: 0.9rem;
    min-width: 250px;
  }
`;

const StatsContainer = styled(motion.div)`
  display: grid;
  /* 5 cards: 3+2 layout for better balance */
  grid-template-columns: repeat(3, 1fr);
  gap: 2rem;
  max-width: 900px;
  margin: 3rem auto 0;
  
  /* Target the 4th and 5th items to center them in second row */
  & > :nth-child(4) {
    grid-column: 1 / 2;
  }
  & > :nth-child(5) {
    grid-column: 3 / 4;
  }
  
  @media (max-width: 1000px) {
    /* Switch to auto-fit for medium screens */
    grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
    & > :nth-child(4),
    & > :nth-child(5) {
      grid-column: auto;
    }
  }
  
  @media (max-width: 768px) {
    grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
    gap: 1rem;
    margin: 2rem auto 0;
    max-width: 100%;
  }
  
  @media (max-width: 480px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 0.8rem;
    margin: 1.5rem auto 0;
    padding: 0 0.5rem;
  }
  
  @media (max-width: 360px) {
    grid-template-columns: 1fr;
    gap: 0.8rem;
  }
`;

const StatItem = styled(motion.div)`
  text-align: center;
  padding: 1.5rem 1rem;
  background: ${corporateTheme.cardBackground};
  border-radius: 15px;
  backdrop-filter: blur(15px);
  border: 1px solid ${corporateTheme.cardBorder};
  position: relative;
  overflow: hidden;
  box-shadow: ${corporateTheme.cardShadow};
  
  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: ${corporateTheme.primaryGradient};
  }
  
  @media (max-width: 768px) {
    padding: 1rem 0.5rem;
    border-radius: 12px;
  }
  
  @media (max-width: 480px) {
    padding: 0.8rem 0.4rem;
    border-radius: 10px;
  }
`;

const StatNumber = styled.div`
  font-size: 2rem;
  font-weight: 800;
  color: ${corporateTheme.primary};
  margin-bottom: 0.5rem;
  
  @media (max-width: 768px) {
    font-size: 1.5rem;
    margin-bottom: 0.3rem;
  }
  
  @media (max-width: 480px) {
    font-size: 1.3rem;
    margin-bottom: 0.2rem;
  }
`;

const StatLabel = styled.div`
  font-size: 0.9rem;
  color: ${corporateTheme.textSecondary};
  font-weight: 500;
  line-height: 1.3;
  
  @media (max-width: 768px) {
    font-size: 0.8rem;
    line-height: 1.2;
  }
  
  @media (max-width: 480px) {
    font-size: 0.7rem;
    line-height: 1.1;
  }
`;

const ResponsiveButtonText = styled.span`
  .mobile-text {
    display: none;
  }
  
  @media (max-width: 768px) {
    .desktop-text {
      display: none;
    }
    .mobile-text {
      display: inline;
    }
  }
`;

const HeroSection = () => {
  const { openModal } = useVoiceChat();
  
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.3
      }
    }
  };

  const itemVariants = {
    hidden: { y: 50, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.8,
        ease: "easeOut"
      }
    }
  };

  return (
    <HeroContainer id="hero">
      <BackgroundImage />
      
      <HeroContent>
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          <Badge variants={itemVariants}>
            {corporateLabels.highConverting} Digital {corporateLabels.affiliate} Program
          </Badge>
          
          <MainHeadline variants={itemVariants}>
            Start Earning with GrowTech Affiliates
          </MainHeadline>
          
          <EarningHighlight variants={itemVariants}>
            <EarningItem>
              <EarningLabel>Earn Up To</EarningLabel>
              <EarningAmount>€5K</EarningAmount>
              <EarningDescription>Monthly Bonus</EarningDescription>
            </EarningItem>
            
            <EarningItem>
              <EarningLabel>Up To</EarningLabel>
              <EarningAmount>45%</EarningAmount>
              <EarningDescription>Revenue Share</EarningDescription>
            </EarningItem>
          </EarningHighlight>
          
          <Description variants={itemVariants}>
            Join hundreds of successful affiliates earning with premium digital product offers. 
            Get access to high-converting e-commerce, SaaS, and digital service campaigns with 
            industry-leading commissions and weekly payments.
          </Description>
          
          <ButtonGroup variants={itemVariants}>
            <PrimaryButton
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => {
                const contactSection = document.querySelector('#contact');
                if (contactSection) {
                  contactSection.scrollIntoView({ behavior: 'smooth' });
                } else {
                  window.location.href = '/contact';
                }
              }}
            >
              <ResponsiveButtonText>
                <span className="desktop-text">{corporateLabels.startEarning} Now</span>
                <span className="mobile-text">{corporateLabels.startEarning}</span>
              </ResponsiveButtonText>
            </PrimaryButton>
            
            <SecondaryButton
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={openModal}
            >
              <ResponsiveButtonText>
                <span className="desktop-text">{corporateLabels.consultExpert} - Ask Questions</span>
                <span className="mobile-text">{corporateLabels.consultExpert}</span>
              </ResponsiveButtonText>
            </SecondaryButton>
            
            <SecondaryButton
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => document.querySelector('#stats').scrollIntoView({ behavior: 'smooth' })}
            >
              <ResponsiveButtonText>
                <span className="desktop-text">{corporateLabels.viewRates} Commission Rates</span>
                <span className="mobile-text">{corporateLabels.viewRates}</span>
              </ResponsiveButtonText>
            </SecondaryButton>
          </ButtonGroup>
          
          <StatsContainer variants={itemVariants}>
            <StatItem
              whileHover={{ scale: 1.05, y: -5 }}
              transition={{ duration: 0.3 }}
            >
              <StatNumber>€2M+</StatNumber>
              <StatLabel>Paid to Affiliates Monthly</StatLabel>
            </StatItem>
            
            <StatItem
              whileHover={{ scale: 1.05, y: -5 }}
              transition={{ duration: 0.3 }}
            >
              <StatNumber>500+</StatNumber>
              <StatLabel>Active Partners</StatLabel>
            </StatItem>
            
            <StatItem
              whileHover={{ scale: 1.05, y: -5 }}
              transition={{ duration: 0.3 }}
            >
              <StatNumber>72H</StatNumber>
              <StatLabel>Average Approval Time</StatLabel>
            </StatItem>
            
            <StatItem
              whileHover={{ scale: 1.05, y: -5 }}
              transition={{ duration: 0.3 }}
            >
              <StatNumber>24/7</StatNumber>
              <StatLabel>Affiliate Support</StatLabel>
            </StatItem>
            
            <StatItem
              whileHover={{ scale: 1.05, y: -5 }}
              transition={{ duration: 0.3 }}
            >
              <StatNumber>Weekly</StatNumber>
              <StatLabel>Payment Schedule</StatLabel>
            </StatItem>
          </StatsContainer>
        </motion.div>
      </HeroContent>
    </HeroContainer>
  );
};

export default HeroSection; 